import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ApiError } from "@/api/errors";

/**
 * Resolve a gift SKU to its base components (base SKU × multiplicity) via the
 * listing catalog, so the rule builder can preview what stock a gift reserves.
 *
 * A gift that is not a listing (a single base SKU) has no listing row → the
 * endpoint 404s. The client middleware throws ApiError on every non-2xx, so
 * a 404 must be caught explicitly and resolved to [] rather than surfacing as
 * a query error (TC61) — any other status still propagates as a real error.
 */
export function useGiftExpansion(code: string | null) {
  return useQuery({
    queryKey: ["promotion", "gift-expansion", code ?? ""],
    queryFn: async () => {
      try {
        const { data } = await apiClient.GET("/v1/listings/{code}", {
          params: { path: { code: code as string } },
        });
        return data?.components ?? [];
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return [];
        throw err;
      }
    },
    enabled: Boolean(code),
    retry: false,
    staleTime: 30_000,
  });
}
